import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { APP_VERSION } from '_constants';
import { UnixTimeCode } from '../types/Utility';

export type ModalState = {
    /**
     * The UNIX time  when the add profile modal should be shown again.
     */
    showAfter: UnixTimeCode | null;
    recommendedAppVersionDismissedAt: UnixTimeCode | null;
    /**
     * The initial app version when the user installed the app.
     * If undefined it indicates pre 2.12.7
     */
    initialAppVersion: string | undefined;

    /**
     * The UNIX time when a modal could be shown again. We do this to avoid nagging on the user.
     */
    showModalAfter: UnixTimeCode | null;

    showParkingFeedbackModalAfter: UnixTimeCode | null;

    /* Deprecated feedbacks tracking we keep this incase we want to use them in the future for something! */
    noticesListFeedbackGivenAt: UnixTimeCode | null;
    noticesSingleFeedbackGivenAt: UnixTimeCode | null;
    activityNewAbortQuestionAskedAt: UnixTimeCode | null;
    activitySuggestionAskedAt: UnixTimeCode | null;
};

const initialState: ModalState = {
    showAfter: null,
    showModalAfter: null,
    showParkingFeedbackModalAfter: null,
    activityNewAbortQuestionAskedAt: null,
    activitySuggestionAskedAt: null,
    recommendedAppVersionDismissedAt: null,
    initialAppVersion: APP_VERSION,
    noticesListFeedbackGivenAt: null,
    noticesSingleFeedbackGivenAt: null,
};

/**
 * This slice is used for storing all information about questions that are asked to the user, and general tracking information.
 */
const modalSlice = createSlice({
    name: 'addProfileModal', // Legacy name
    initialState,
    reducers: {
        dismissAddProfile: (state, action: PayloadAction<UnixTimeCode>) => {
            if (state.showAfter && state.showAfter > action.payload) {
                return;
            }
            state.showAfter = action.payload;
            state.showModalAfter = moment().add({ days: 4 }).unix();
        },
        askActivityNewAbort: (state, action: PayloadAction<UnixTimeCode>) => {
            state.activityNewAbortQuestionAskedAt = action.payload;
        },
        askActivitySuggestionAbort: (state, action: PayloadAction<UnixTimeCode>) => {
            state.activitySuggestionAskedAt = action.payload;
        },
        dismissRecommendedAppVersion: (state, action: PayloadAction<UnixTimeCode>) => {
            state.recommendedAppVersionDismissedAt = action.payload;
        },
        resetRecommendedAppVersion: (state) => {
            state.recommendedAppVersionDismissedAt = null;
        },
        gaveNoticesListFeedback: (state, action: PayloadAction<UnixTimeCode>) => {
            state.noticesListFeedbackGivenAt = action.payload;
        },
        gaveNoticesSingleFeedback: (state, action: PayloadAction<UnixTimeCode>) => {
            state.noticesSingleFeedbackGivenAt = action.payload;
        },
        dismissModal: (state) => {
            state.showModalAfter = moment().add({ days: 4 }).unix();
        },
        setShowParkingFeedbackModalAfter: (state, action: PayloadAction<UnixTimeCode>) => {
            if (state.showParkingFeedbackModalAfter) {
                state.showParkingFeedbackModalAfter = Math.max(state.showParkingFeedbackModalAfter, action.payload);
            } else {
                state.showParkingFeedbackModalAfter = action.payload;
            }
        },
    },
});

export const {
    dismissAddProfile,
    askActivityNewAbort,
    askActivitySuggestionAbort,
    dismissRecommendedAppVersion,
    resetRecommendedAppVersion,
    gaveNoticesListFeedback,
    gaveNoticesSingleFeedback,
    dismissModal,
    setShowParkingFeedbackModalAfter,
} = modalSlice.actions;

export default modalSlice.reducer;
